import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./EmployeeTrainingIdentified.css";
import "./TrainingManDaysCalculation.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { trainingNeedsAPI, trngCourseAPI } from "./api";
import { getDistinctFilters } from "../Components/Menu/Branch";
import TrngCourseViewer from "../Components/Viewers/TrngCourseViewer";
import FuncAreaViewer from "../Components/Viewers/FuncAreaViewer";
import DataTable from "./DataTable";

const Report1 = () => {
  const [activeView, setActiveView] = useState(null);

  const [trainingCourseName, setTrainingCourseName] = useState("");
  const [trngCourseCode, setTrngCourseCode] = useState("");
  const [functionArea, setFunctionArea] = useState("");
  const [funcSubAreaCode, setFuncSubAreaCode] = useState("");
  const [status, setStatus] = useState("All");
  const [location, setLocation] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);

  const [summaryRows, setSummaryRows] = useState([]);
  const [courseMap, setCourseMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [branches, setBranches] = useState([]);

  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showCourseViewer, setShowCourseViewer] = useState(false);
  const [showFuncAreaViewer, setShowFuncAreaViewer] = useState(false);

  useEffect(() => {
    getDistinctFilters().then(({ branches: b }) => setBranches(b));
  }, []);

  const handleCourseSelect = (row) => {
    setTrainingCourseName(row.trng_course_name ?? "");
    setTrngCourseCode(row.trng_course_code ?? "");
    setShowCourseViewer(false);
  };

  const handleFuncAreaSelect = (row) => {
    setFunctionArea(row.func_sub_area_name ?? "");
    setFuncSubAreaCode(row.func_sub_area_code ?? "");
    setShowFuncAreaViewer(false);
  };

  const buildParams = () => {
    const p = {};
    if (trngCourseCode) p.trng_course_code = trngCourseCode;
    if (funcSubAreaCode) p.func_sub_area_code = funcSubAreaCode;
    if (location) p.locn = location;
    if (status && status !== "All") p.status = status;
    if (fromDate) p.from_date = fromDate.toLocaleDateString('en-CA');
    if (toDate)   p.to_date   = toDate.toLocaleDateString('en-CA');
    console.log('[Report1] buildParams =>', p);
    return p;
  };

  const handleDetails = async () => {
    setDetailLoading(true);
    setErrorMsg(null);
    const params = buildParams();
    console.log('[Report1] handleDetails: calling getDetails with', params);
    try {
      const data = await trainingNeedsAPI.getDetails(params);
      console.log('[Report1] handleDetails: response count =', Array.isArray(data) ? data.length : data, Array.isArray(data) && data.length > 0 ? 'first row =>' : '', Array.isArray(data) && data.length > 0 ? data[0] : '');
      if (!Array.isArray(data)) console.warn('[Report1] handleDetails: unexpected response shape =>', data);
      setDetailRows(Array.isArray(data) ? data : []);
      setActiveView("details");
    } catch (err) {
      console.error('[Report1] handleDetails: FAILED =>', err?.message ?? err, err?.response?.data ?? '');
      setErrorMsg("Failed to load details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSummary = async () => {
    setLoading(true);
    setErrorMsg(null);
    const params = buildParams();
    console.log('[Report1] handleSummary: calling getSummary with', params);
    try {
      const [summaryData, courseData] = await Promise.all([
        trainingNeedsAPI.getSummary(params),
        trngCourseAPI.getAll(),
      ]);
      console.log('[Report1] handleSummary: summaryData count =', Array.isArray(summaryData) ? summaryData.length : summaryData);
      console.log('[Report1] handleSummary: courseData count =', Array.isArray(courseData) ? courseData.length : courseData);
      if (!Array.isArray(summaryData)) console.warn('[Report1] handleSummary: unexpected summaryData shape =>', summaryData);
      const map = {};
      if (Array.isArray(courseData)) {
        for (const c of courseData) map[String(c.trng_course_code)] = c.trng_course_name;
      }
      setCourseMap(map);
      setSummaryRows(Array.isArray(summaryData) ? summaryData : []);
      setActiveView("summary");
    } catch (err) {
      console.error('[Report1] handleSummary: FAILED =>', err?.message ?? err, err?.response?.data ?? '');
      setErrorMsg("Failed to load summary.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const today = new Date().toLocaleDateString("en-GB");
    if (activeView === "summary") {
      const headers = ["Sr. No.", "Training Course Name", "Total Identified", "Total Attended", "Yet To Attend"];
      const data = summaryRows.map((row, idx) => [
        idx + 1,
        courseMap[String(row.trng_course_code)] ?? row.trng_course_code ?? "",
        row.total_identified,
        row.total_attended,
        row.yet_to_attend,
      ]);
      const aoa = [
        ["Report Title : Training Need Identification along with Status"],
        [`Report Date : ${today}`],
        [],
        headers,
        ...data,
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), "Training_Need_Identification_Status_Summary.xlsx");
    } else if (activeView === "details") {
      const headers = ["Sr. No.", "Training Course Name", "Employee Name", "Salary Code", "Functional Area Name", "Location", "Zone", "Status"];
      const data = detailRows.map((row, idx) => [
        idx + 1,
        row.trng_course_name ?? "",
        row.emp_name ?? "",
        row.sal_code ?? "",
        row.func_sub_area_name ?? "",
        row.locn ?? "",
        row.zone ?? "",
        row.status ?? "",
      ]);
      const aoa = [
        ["Report Title : Training Need Identification along with Status"],
        [`Report Date : ${today}`],
        [],
        headers,
        ...data,
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), "Training_Need_Identification_Status_Details.xlsx");
    }
  };

  const handleReset = () => {
    setTrainingCourseName("");
    setTrngCourseCode("");
    setFunctionArea("");
    setFuncSubAreaCode("");
    setStatus("All");
    setLocation("");
    setFromDate(null);
    setToDate(null);
    setActiveView(null);
    setSummaryRows([]);
    setCourseMap({});
    setDetailRows([]);
    setErrorMsg(null);
  };

  const summaryColumns = [
    { header: "Sr. No.", renderCell: (_v, _row, idx) => idx + 1 },
    { header: "Training Course Name", accessor: (row) => courseMap[String(row.trng_course_code)] ?? row.trng_course_code ?? "" },
    { header: "Total Identified", accessor: (row) => row.total_identified ?? "" },
    { header: "Total Attended", accessor: (row) => row.total_attended ?? "" },
    { header: "Yet To Attend", accessor: (row) => row.yet_to_attend ?? "" },
  ];

  const detailColumns = [
    { header: "Sr. No.", renderCell: (_v, _row, idx) => idx + 1 },
    { header: "Training Course Name", accessor: (row) => row.trng_course_name ?? "" },
    { header: "Employee Name", accessor: (row) => row.emp_name ?? "" },
    { header: "Salary Code", accessor: (row) => row.sal_code ?? "" },
    { header: "Functional Area Name", accessor: (row) => row.func_sub_area_name ?? "" },
    { header: "Location", accessor: (row) => row.locn || row.branch || "" },
    { header: "Zone", accessor: (row) => row.zone ?? "" },
    { header: "Status", accessor: (row) => row.status ?? "" },
  ];

  return (
    <div className="bd-container">

      {/* FILTERS */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
        <table style={{ fontFamily: "GEG-Body", fontSize: 12.5 }}>
        <tbody>
          <tr>
            <td className="bd-label" style={{ paddingRight: 6 }}>Function Area Name</td>
            <td style={{ paddingRight: 40 }}>
              <input className="bd-input" style={{ width: 160 }} type="text" value={functionArea} readOnly onFocus={(e) => e.target.blur()} />
              <button className="btn btn-outline" type="button" onClick={() => setShowFuncAreaViewer(true)}>Get</button>
              <button className="btn btn-secondary" type="button" onClick={() => { setFunctionArea(""); setFuncSubAreaCode(""); }}>Clear</button>
            </td>
            <td className="bd-label" style={{ paddingRight: 6 }}>Training Course Name</td>
            <td>
              <input className="bd-input" style={{ width: 160 }} type="text" value={trainingCourseName} readOnly onFocus={(e) => e.target.blur()} />
              <button className="btn btn-outline" type="button" onClick={() => setShowCourseViewer(true)}>Get</button>
              <button className="btn btn-secondary" type="button" onClick={() => { setTrainingCourseName(""); setTrngCourseCode(""); }}>Clear</button>
            </td>
          </tr>
          <tr>
            <td className="bd-label" style={{ paddingRight: 6 }}>LocN</td>
            <td style={{ paddingRight: 40 }}>
              <select className="bd-input" style={{ width: 160 }} value={location} onChange={(e) => setLocation(e.target.value)}>
                <option value="">-- Select --</option>
                {branches.map((b) => (<option key={b} value={b}>{b}</option>))}
              </select>
            </td>
            <td className="bd-label" style={{ paddingRight: 6 }}>Status</td>
            <td>
              <select className="bd-input" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option>All</option>
                <option>Attended</option>
                <option>Not Attended</option>
              </select>
            </td>
          </tr>
          <tr>
            <td className="bd-label" style={{ paddingRight: 6 }}>Needs Identified From Date*</td>
            <td style={{ paddingRight: 40 }}>
              <div className="tmd-date-wrap" onClick={() => setFromDateOpen(true)}>
                <DatePicker
                  open={fromDateOpen}
                  onClickOutside={() => setFromDateOpen(false)}
                  selected={fromDate}
                  onChange={(date) => { setFromDate(date); setFromDateOpen(false); }}
                  dateFormat="dd/MM/yyyy"
                  className="tmd-date-input"
                  placeholderText="Select from date"
                  showIcon
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  customInput={<input readOnly />}
                />
              </div>
            </td>
            <td className="bd-label" style={{ paddingRight: 6 }}>To Date*</td>
            <td>
              <div className="tmd-date-wrap" onClick={() => setToDateOpen(true)}>
                <DatePicker
                  open={toDateOpen}
                  onClickOutside={() => setToDateOpen(false)}
                  selected={toDate}
                  onChange={(date) => { setToDate(date); setToDateOpen(false); }}
                  dateFormat="dd/MM/yyyy"
                  className="tmd-date-input"
                  placeholderText="Select to date"
                  minDate={fromDate}
                  showIcon
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  customInput={<input readOnly />}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      {/* ACTION ROW */}
      <div className="action-row">
        <button className="btn btn-outline" onClick={handleReset}>Reset</button>
        <select className="bd-input" style={{ width: "auto" }}>
          <option value="">--- Sort On: ---</option>
          <option>Training Course Name</option>
          <option>Location</option>
          <option>Zone</option>
          <option>Status</option>
        </select>
        <select className="bd-input" style={{ width: "auto" }}>
          <option>ExactMatch</option>
          <option>Contains</option>
        </select>
        <select className="bd-input" style={{ width: "auto" }}>
          <option>Any</option>
          <option>All</option>
        </select>
        <button
          className={`btn btn-outline`}
          onClick={handleSummary}
          disabled={loading}
        >
          {loading && activeView !== "details" ? "Loading…" : "Summary"}
        </button>
        <button
          className={`btn btn-outline`}
          onClick={handleDetails}
          disabled={detailLoading}
        >
          {detailLoading ? "Loading…" : "Details"}
        </button>
      </div>

      {errorMsg && <p className="bd-error">{errorMsg}</p>}

      {/* TABLE AREA */}
      {activeView !== null && (
        <div className="bd-details">
          <p className="bd-section-title">
            {activeView === "summary" ? "Training Need Identification Status — Summary" : "Training Need Identification Status — Details"}
          </p>
          <DataTable
            columns={activeView === "summary" ? summaryColumns : detailColumns}
            data={activeView === "summary" ? summaryRows : detailRows}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No data to display"
            rowKey={(_row, idx) => idx}
          />
          {(activeView === "summary" ? summaryRows : detailRows).length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="btn btn-outline" onClick={handleExport}>Export to Excel</button>
            </div>
          )}
        </div>
      )}

      {/* TrngCourse Viewer Modal */}
      {showCourseViewer && (
        <div className="facviewer-overlay" onClick={() => setShowCourseViewer(false)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setShowCourseViewer(false)}>&times;</button>
            <TrngCourseViewer selectable onSelect={handleCourseSelect} autoLoad />
          </div>
        </div>
      )}

      {/* FuncArea Viewer Modal */}
      {showFuncAreaViewer && (
        <div className="facviewer-overlay" onClick={() => setShowFuncAreaViewer(false)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setShowFuncAreaViewer(false)}>&times;</button>
            <FuncAreaViewer selectable onSelect={handleFuncAreaSelect} />
          </div>
        </div>
      )}

    </div>
  );
};

export default Report1;
